import { ArrowRight, MessageCircle } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/site-data";
import FadeIn from "@/components/FadeIn";

export default function Hero() {
  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(
    "Hi DreamFly Travels, I'd like a quote."
  )}`;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Background image */}
      <img
        src={IMAGES.hero}
        alt="Maldives overwater villa at sunset"
        className="absolute inset-0 h-full w-full object-cover"
        loading="eager"
      />
      <div className="absolute inset-0 hero-vignette" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[#0F3D3E]/40 via-transparent to-[#0F3D3E]/10" />

      <div className="relative z-10 min-h-[100svh] max-w-7xl mx-auto px-5 sm:px-8 flex flex-col">
        {/* Spacer for navbar */}
        <div className="h-24 sm:h-28" />

        <div className="flex-1 flex items-center">
          <div className="max-w-3xl">
            <FadeIn delay={100}>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 px-4 py-1.5 text-xs tracking-[0.2em] uppercase">
                <span className="size-1.5 rounded-full bg-[#7A8F6B]" />
                India-based · International Specialists
              </div>
            </FadeIn>

            <FadeIn delay={250}>
              <h1 className="mt-6 font-display text-white text-[40px] leading-[1.05] sm:text-[56px] lg:text-[68px] tracking-tight">
                Luxury International
                <br />
                Travel Packages{" "}
                <span className="italic text-[#F4EDE4]">from India.</span>
              </h1>
            </FadeIn>

            <FadeIn delay={400}>
              <p className="mt-6 max-w-2xl text-white/90 text-base sm:text-lg leading-relaxed">
                <span className="font-medium tracking-wide">
                  Maldives · Europe · UK · Thailand · Africa
                </span>
                <br className="hidden sm:block" />
                <span className="text-white/80">
                  Curated holidays with the best pricing and trusted global DMC
                  partners.
                </span>
              </p>
            </FadeIn>

            <FadeIn delay={550}>
              <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <a
                  href="#packages"
                  data-testid="hero-cta-packages"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#F4EDE4] text-[#0F3D3E] px-7 py-3.5 text-sm sm:text-base font-medium hover:bg-white transition-colors"
                >
                  Explore Packages
                  <ArrowRight
                    size={18}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#2C7A7B] text-white px-7 py-3.5 text-sm sm:text-base font-medium hover:bg-[#7A8F6B] transition-colors shadow-[0_10px_40px_-12px_rgba(44,122,123,0.6)]"
                >
                  <MessageCircle size={18} />
                  Get a Quote on WhatsApp
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={750}>
              <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4 text-white/80 text-sm">
                <div className="flex items-center gap-2">
                  <span className="size-1 rounded-full bg-white/60" />
                  Customised holiday packages
                </div>
                <div className="flex items-center gap-2">
                  <span className="size-1 rounded-full bg-white/60" />
                  Hotels · Transfers · Visa
                </div>
                <div className="flex items-center gap-2">
                  <span className="size-1 rounded-full bg-white/60" />
                  Group tours & FIT
                </div>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* Bottom price card */}
        <div className="pb-10 sm:pb-14">
          <FadeIn delay={900}>
            <div
              data-testid="hero-feature-card"
              className="inline-flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 px-5 sm:px-7 py-4 sm:py-5"
            >
              <div className="text-white">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">
                  Featured · Maldives
                </div>
                <div className="mt-1 font-display text-2xl sm:text-3xl">
                  3 Nights · 4 Days
                </div>
              </div>
              <div className="h-px sm:h-10 w-full sm:w-px bg-white/20" />
              <div className="text-white">
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">
                  Starting from
                </div>
                <div className="mt-1 font-display text-2xl sm:text-3xl">
                  ₹80,000 <span className="text-sm font-sans-ui text-white/70">/ pp</span>
                </div>
              </div>
              <a
                href="#packages"
                data-testid="hero-feature-link"
                className="sm:ml-4 text-[#F4EDE4] underline underline-offset-4 decoration-white/40 hover:decoration-white text-sm"
              >
                View itinerary →
              </a>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}
